import { useScheduledReportStore, reportStatus } from '@/lib/stores/scheduledReport';
import { useCreditStore } from '@/lib/stores/credit';
import { useImageStore } from '@/lib/stores/image';
import { toast } from "react-toastify";
import { batch } from 'react-redux'
import { z } from 'zod';
import { AxiosError } from 'axios'
import { dispatchBatchActions } from "@/helpers/OfflineCommonHandlers";
import localforage from "localforage";
import api from './api';
import { omit } from 'lodash';
import __ from '@/helpers/translation';

//#region Action types

  export const SET_REPORT = 'SET_REPORT';
  export const SET_REPORT_ANSWERS = 'SET_REPORT_ANSWERS';
  export const UPDATE_REPORT_ANSWER = 'UPDATE_REPORT_ANSWER';
  export const SET_REPORT_CHECKED = 'SET_REPORT_CHECKED';
  export const SET_REPORT_GENERATED = 'SET_REPORT_GENERATED';
  export const ADD_ANSWER_IMAGE = 'ADD_ANSWER_IMAGE';
  export const REMOVE_ANSWER_IMAGE = 'REMOVE_ANSWER_IMAGE';

//#endregion

//#region Scheduling / starting report actions (refactored)

  /**
   * Schedules an inspection by performing the following steps:
   * 1. Checks if the user is online.
   * 2. Parses saved company and user data from local storage.
   * 3. Validates the parsed company and user data against predefined schemas.
   * 4. Retrieves location and sublocation IDs from the server.
   * 5. Generates offline reports and updates local storage with the new location and sublocation data.
   * 
   * @async
   * @function scheduleInspection
   * @param {Object} props - The inspection data.
   * @param {number} props.locationId - The ID of the location.
   * @returns {Promise<void>} A promise that resolves when the inspection is successfully scheduled or rejects with an error message.
   * @throws Will throw an error if the user is offline, or if the company or user data is invalid.
   */
  export const scheduleInspection = async ({ locationId }) => {
    
    if(!window.navigator.onLine) {
      toast.error(__('app.toasts.error_scheduling_needs_to_be_online'));
      return;
    }

    const { generateOfflineReports } = useScheduledReportStore.getState();

    //#region 1. Parse saved company and user data

      const company = await localforage.getItem('bs_company');
      const user = await localforage.getItem('bs_user');

      const companySchema = z.object({
        id: z.number().int().positive(),
        name: z.string().nullable(),
        zip: z.string().nullable(),
        city: z.string().nullable(),
        address: z.string().nullable(),
        telephone: z.string().nullable(),
        status: z.number().int().min(0),
        reports_count: z.number().int().min(0).nullable(),
      })

      const userSchema = z.object({
        id: z.number().int().positive(),
        email: z.string().email(),
        name: z.string().nullable(),
        language: z.string().nullable(),
        last_active_terms_accepted: z.boolean(),
        new_connections: z.number().int().min(0),
      })

      if(!companySchema.safeParse(company).success) {
        toast.error(__('app.toasts.error_invalid_company_details'));
        return;
      }

      if(!userSchema.safeParse(user).success) {
        toast.error(__('app.toasts.error_invalid_credentials'));
        return;
      }

    //#endregion

    //#region 2. Get the location and sublocation IDs

      const existingLocation = await localforage.getItem('bs_location');
      if(existingLocation !== null) {
        toast.info(__('app.toasts.info_scheduled_locations_will_be_overwritten'));
      }

      const locationResponse = await window.axios.get(api.locations.index.replace(':company', company.id));
      const location = locationResponse.data.locations.find(location => location.id === locationId);

      if(!location) {
        toast.error(__('app.toasts.error_location_not_found'));
        return;
      }

      const sublocationResponse = await window.axios.get(api.sublocations.index.replace(':location', location.id));
      const sublocationIds = sublocationResponse.data.sublocations.map(sublocation => sublocation.id);

      console.log({
        location,
        sublocations: sublocationResponse.data.sublocations
      })

    //#endregion

    const schedulePromise = Promise.all([
      generateOfflineReports(company, user, location, sublocationIds),
      localforage.setDriver(localforage.INDEXEDDB),
      localforage.setItem('bs_sublocations', sublocationResponse.data.sublocations),
      localforage.setItem('bs_location', location),
    ])

    await toast.promise(schedulePromise, {
      pending: __('app.toasts.promise_scheduling_pending'),
      success: __('app.toasts.promise_scheduling_success'),
    })
  }

  /**
   * Starts an inspection process.
   *
   * This function handles both online and offline inspection processes. If the user is offline,
   * it will attempt to start an inspection using the locally stored scheduled reports.
   * If the user is online, it will attempt to create a new inspection via the API and
   * add the report to the scheduled reports in the store.
   *
   * @param {Object} data - The inspection data.
   * @param {number} data.company_id - The ID of the company.
   * @returns {Promise<Object>} A promise that resolves to the inspection data.
   */
  export const startInspection = (data) => async (dispatch) => {

    const {
      scheduledReports,
      updateReportStatus,
      addReport,
    } = useScheduledReportStore.getState();
    
    const { creditHandlers: { reserveCreditForScan }} = useCreditStore.getState();

    // Offline mode
    if(!window.navigator.onLine) {
      
      if(!scheduledReports.length) {
        return Promise.reject('No scheduled reports found in local storage.');
      }

      const selectedReport = scheduledReports.find(report => {
        const { sublocation_id, removal_type_id } = report.data.relation_ids;
        return sublocation_id === data.sublocation_id && removal_type_id === data.removal_id;
      });

      if(!selectedReport) {
        return Promise.reject('No matching report found.');
      }

      //#region Update store and direct IndexedDB

        try {
          await updateReportStatus(selectedReport.data.id, reportStatus.ACTIVE);
        } catch (err) {
          console.error('Error updating report status:', err);
          return Promise.reject('Could not update the report status in local storage.');
        }

        try {
          await Promise.all([
            localforage.setDriver(localforage.INDEXEDDB),
            localforage.setItem('bs_inspection', omit(selectedReport.data, 'answers')),
            localforage.setItem('bs_inspection_answers', selectedReport.data.answers),
          ]);
        } catch (error) {
          return Promise.reject('Could not update IndexedDB with selected report and answers.');
        }

        // Reserve credits
        try {
          reserveCreditForScan({ companyId: data.company_id });
        } catch(err) {
          console.error('Error reserving credits:', err);
          return Promise.reject(__('app.credits.not_enough_credits_error'));
        }

      //#endregion

      batch(() => {
        dispatch(setReportAnswers(selectedReport.data.answers));
        dispatch(setActiveReport(selectedReport.data));
      });

      return Promise.resolve({ report: selectedReport.data, answers: selectedReport.data.answers });
    } 

    // Online mode
    else {
      try {
        const response = await window.axios.post(api.inspections.store, { ...data, localCreditsUsed: false });

        try {
          reserveCreditForScan({ companyId: response.data.report.relation_ids.company_id });
        }
        catch(err) {
          console.error('Error reserving credits:', err);
          return Promise.reject(__('app.credits.not_enough_credits_error'));
        }

        // Add the report to the store
        addReport({
          status: reportStatus.ACTIVE,
          data: response.data.report,
        });

        batch(() => {
          dispatch(setReportAnswers(response.data.answers));
          dispatch(setActiveReport(response.data.report));
        })

        return Promise.resolve({
          report: response.data.report,
          answers: response.data.answers
        });
      } 
      catch (error) {
        console.error('Error starting inspection online:', error);
        return Promise.reject(error);
      }
    }
  };

  /**
   * Retrieves the active inspection data from the Zustand store.
   * If no active inspection is found, the function will reject the promise.
   * 
   * @returns {Promise<Object>} A promise that resolves to the active inspection data or rejects with an error message.
   */
  export const getActiveInspection = () => async dispatch => {

    const { scheduledReports } = useScheduledReportStore.getState();
    const activeReport = scheduledReports.find(report => report.status === reportStatus.ACTIVE);
    if(!activeReport) {
      return Promise.reject('No active report found.');
    }

    try {
      batch(() => {
        dispatch(setActiveReport(activeReport.data));
        dispatch(setReportAnswers(activeReport.data.answers));
      });

      return Promise.resolve({
        report: activeReport.data,
        answers: activeReport.data.answers
      });
    }
    catch(err) {
      return Promise.reject('Failed to set the active report.');
    }
  }

//#endregion

//#region Action creators (not refactored)

  export const setActiveReport = (report) => ({
    type: SET_REPORT,
    report
  });

  export const setReportAnswers = (answers) => ({
    type: SET_REPORT_ANSWERS,
    answers
  });

  export const updateReportAnswer = (answer) => ({
    type: UPDATE_REPORT_ANSWER,
    answer
  });

  export const addAnswerImage = (answer, image) => ({
    type: ADD_ANSWER_IMAGE,
    answer,
    image
  });

  export const removeAnswerImage = (answer, image) => ({
    type: REMOVE_ANSWER_IMAGE,
    answer,
    image
  });

  export const setReportChecked = (reportChecked) => ({
    type: SET_REPORT_CHECKED,
    reportChecked
  });

  export const setReportGenerated = (isGenerated) => ({
    type: SET_REPORT_GENERATED,
    isGenerated
  });

  /**
   * Action creator to set the report check flag.
   * 
   * @param {boolean} isChecked - The flag indicating whether the report is checked.
   * @returns {Function} A thunk function that dispatches the appropriate action based on the online status.
   */
  export const setReportCheckFlag = (isChecked) => dispatch => {
    if(!window.navigator.onLine) dispatch(setReportChecked(true));
    else dispatch(setReportChecked(isChecked));
  }

  /**
   * Action creator that dispatches the setReportGenerated action with the provided completion status.
   *
   * @param {boolean} isCompleted - The completion status of the report generation.
   * @returns {Function} A thunk function that dispatches the setReportGenerated action.
   */
  export const setReportGeneratedFlag = (isCompleted) => dispatch => {
    dispatch(setReportGenerated(isCompleted));
  }

//#endregion

//#region Saving + stopping inspections (refactored)

  /**
   * Saves the progress of an inspection.
   * If online, it sends the data to the server. If offline, it saves the data locally in IndexedDB.
   *
   * @deprecated This function is deprecated and should not be used anymore.
   * 
   * @async
   * @function saveInspectionProgress
   * @param {Object} report - The inspection report object.
   * @param {Array} answers - The answers related to the inspection.
   * @returns {Promise<string>} A promise that resolves to a success message or rejects with an error message.
   */
  export const saveInspectionProgress = async (report, answers) => {
      
    // Prepare the answer progress data
    const answerProgress = answers.reduce((acc, question) => {
      acc[question.id] = question.answer;
      if (question.subquestion) {
        acc[question.subquestion.id] = question.subquestion.answer;
      }
      return acc;
    }, {});

    // If online, send the progress to the server
    if (window.navigator.onLine) {
      try {
        console.log('Submitting progress online for report ID:', report.id);
        const response = await window.axios.post(api.inspections.update.replace(':report', report.id), {
          answers: answerProgress,
          '_method': 'PATCH',
        });

        console.log('Inspection progress updated online:', response.data);
        return Promise.resolve('Inspection progress updated successfully.');
      }
      catch(err) {
        console.error('Error updating inspection progress online:', err);
        return Promise.reject('Failed to update inspection progress.');
      }
    } 

    // If offline, save the progress locally
    try {
      await localforage.setDriver(localforage.INDEXEDDB);
      await localforage.setItem('bs_inspection_answers', answerProgress);
    
      return Promise.resolve('Progress saved locally.');
    }
    catch(err) {
      console.error('Error updating inspection progress offline:', err);
      return Promise.reject('Failed to update inspection progress.');
    }

  };

  /**
   * Completes an inspection by either sending data to the server (online)
   * or updating local storage (offline). 
   *
   * @param {number} reportId - The inspection report ID. This is used to retrieve the report from the Zustand store.
   * @param {string[]} emails - An array of email addresses.
   * @returns {Function} A thunk function that handles the inspection completion.
   */
  export const completeInspection = (reportId, emails) => async dispatch => {

    const { scheduledReports } = useScheduledReportStore.getState();
    const report = scheduledReports.find(report => report.data.id === reportId);

    if(!report) {
      return Promise.reject('No report found with the given ID.');
    }
    if(report.status !== reportStatus.ACTIVE) {
      return Promise.reject('The report is not active.');
    }

    if (window.navigator.onLine) {
      return completeInspectionOnline(report.data, emails, dispatch);
    } 

    else return completeInspectionOffline(report.data, emails, dispatch);
  };

  /**
   * Stops an inspection by either sending data to the server (online) or updating the Zustand store offline.
   * 
   * @param {{ id: number, removal: string, version: string, can_send_email: number, answers: [] }} report - The inspection report object.
   * @param {boolean} generateReport - A flag indicating whether to generate a report.
   * @param {string} emails - A comma-separated list of email addresses. 
   * @param {string} explanation - An optional explanation for stopping the inspection.
   * @returns 
   */
  export const stopInspection = ({ id: reportId, answers }, generateReport = false, emails = '', explanation = '') => async dispatch => {
    
    const { 
      credits,
      creditHandlers: { withdrawReservedCredits }
    } = useCreditStore.getState();

    const report = useScheduledReportStore.getState().scheduledReports.find(report => report.data.id === reportId);
    if(!report) {
      return Promise.reject('No report found with the given ID.');
    }

    // Direct upload to the server if possible
    if(window.navigator.onLine) {

      // Withdraw the reserved credits if report is generated
      try {
        if(generateReport) {
          const creditSet = credits.find(credit => credit.companyId.toString() === report.data.relation_ids.company_id.toString());
          if(!creditSet) {
            throw new Error(`No credit set found for company ID: ${report.data.relation_ids.company_id}. Cannot stop inspection.`);
          }
          withdrawReservedCredits({ companyId: report.data.relation_ids.company_id.toString() });
        }
      }
      catch(err) {
        console.error('Error withdrawing reserved credits:', err);
      }

      try {
        const answerIds = answers?.map(answer => answer?.id);
        console.info('Uploading images for answer IDs:', answerIds.join(', '));
        for(const answerId of answerIds) {
          await uploadImages(answerId, reportId);
        }
        console.log('All images uploaded successfully.');
      }
      catch(err) {
        console.error('Error uploading images:', err);
        return Promise.reject('Failed to upload images for the answers.');
      }

      try {
        const response = await window.axios.post(api.inspections.stop.replace(':report', report.data.id), {
          '_method': 'PATCH',
          explanation: explanation,
          generate_report: generateReport,
          emails: generateReport ? emails : undefined
        });
        return response.data;
      }
      catch(err) {
        /** @type {AxiosError} */
        const error = err;
        return Promise.reject(error.response?.data?.message || 'Failed to stop inspection.');
      }
      finally {
        dispatchBatchActions(dispatch, generateReport);
        useScheduledReportStore.getState().removeReport(reportId);
      }
    }

    // Else, we update the status of the report in Zustand so we can sync it later
    try {
      await useScheduledReportStore.getState().stopReport(reportId, {
        explanation: explanation ?? null,
        generateReport: generateReport,
        emails: generateReport ? emails : null
      })
    } catch (error) {
      console.error('Error stopping inspection offline:', error);
      return Promise.reject('Failed to update the report status in local storage.');
    }

    // Reset the Redux store
    try {
      dispatchBatchActions(dispatch, generateReport);
    } catch (error) {
      console.error('Error resetting the Redux store:', error);
      return Promise.reject('Failed to reset the Redux store.');
    }

    // Reset the IndexedDB (old)
    try {
      await removeFromLocalStorage(report.data);
    } catch (error) {
      console.error('Error removing report from local storage:', error);
      return Promise.reject('Failed to remove the report from local storage.');
    }

    return Promise.resolve('Inspection stopped successfully.');
  }

  /**
   * Completes the inspection in online mode by sending data to the server.
   *
   * @param {Object} report - The inspection report object.
   * @param {string[]} emails - An array of email addresses.
   * @param {Function} dispatch - The Redux dispatch function.
   * @returns {Promise<Object>} - A promise that resolves to the server response data.
   */
  const completeInspectionOnline = async (report, emails, dispatch) => {

    const { removeReport } = useScheduledReportStore.getState();
    const { 
      credits,
      creditHandlers: { withdrawReservedCredits }, 
    } = useCreditStore.getState();

    // 1. Upload all images for the different answers binnen toast.promise
    return toast.promise(
      (async () => {
        try {
          
          const answerIds = report?.answers?.map(answer => answer?.id);
          console.info('Uploading images for answer IDs:', answerIds);
          for(const answerId of answerIds) {
            await uploadImages(answerId, report.id);
          }
          console.log('All images uploaded successfully.');

          const answers = report?.answers?.reduce((acc, answer) => {
            acc[answer.id] = answer.answer;
            if (answer.subquestion) {
              acc[answer.subquestion.id] = answer.subquestion.answer;
            }
            return acc;
          }, {});

          const creditSet = credits.find(credit => credit.companyId === report.relation_ids.company_id.toString());
          if(!creditSet) {
            throw new Error(`No credit set found for company ID: ${report.relation_ids.company_id}. Cannot complete inspection.`);
          }

          // Withdraw reserved credits
          const withdrawnCredits = withdrawReservedCredits({ companyId: report.relation_ids.company_id.toString() });

          // 2. Zodra alle uploads zijn voltooid, maak de 'complete' API-aanroep
          const response = await window.axios.post(api.inspections.complete.replace(':report', report.id), {
            '_method': 'PATCH',
            emails: emails,
            answers: answers,
            balanceToken: creditSet.balanceToken,
            creditsToDecrease: withdrawnCredits,
          });

          // 3. Update de Redux state na succesvolle 'complete' aanroep
          batch(() => {
            dispatch(setActiveReport(null));
            dispatch(setReportAnswers(null));
            dispatch(setReportGeneratedFlag(true));
          });

          // 4. Delete de report uit de scheduledReports store
          removeReport(report.id);

          return 'Inspection completed successfully.';
        } 
        catch (err) {
          console.error('Error during inspection completion:', err);
          throw new Error('Failed to complete inspection.');
        }
      })(),
      {
        pending: __('app.toasts.promise_complete_inspection_pending'),
        success: __('app.toasts.promise_complete_inspection_success'),
        error: __('app.toasts.promise_complete_inspection_error'),
      }
    );
  }

  /**
   * Completes the inspection in offline mode by updating local storage.
   *
   * @param {Object} report - The inspection report object.
   * @param {string[]} emails - An array of email addresses.
   * @param {Function} dispatch - The Redux dispatch function.
   * @returns {Promise<string>} - A promise that resolves to a success message.
   */
  const completeInspectionOffline = async (report, emails, dispatch) => {
    
    const { updateReportStatus } = useScheduledReportStore.getState();

    try {

      // Clean up local storage
      await removeFromLocalStorage(report);
      await updateReportStatus(report.id, reportStatus.COMPLETED_OFFLINE);

      // Dispatch Redux actions
      batch(() => {
        dispatch(setActiveReport(null));
        dispatch(setReportAnswers(null));
        dispatch(setReportGeneratedFlag(true));
      });

      console.log('Inspection completed locally.');
      return Promise.resolve('Inspection completed locally.');
    } catch (error) {
      console.error('Error completing inspection offline:', error);
      return Promise.reject(error);
    }
  }

  /**
   * Removes related data from local storage after completing an inspection offline.
   *
   * @param {Object} storedReport - The stored report object.
   * @returns {Promise<void>}
   */
  const removeFromLocalStorage = async (storedReport) => {

    // Remove the report from bs_reports
    try {
      const reports = await localforage.getItem('bs_reports');
      if (reports) {
        const filteredReports = reports.filter(r => r.report.id !== storedReport.id);
        await localforage.setItem('bs_reports', filteredReports);
        console.log('Report removed from scheduled reports.');
      }
    } catch (error) {
      console.error('Error removing report from bs_reports:', error);
    }

    // Remove the sublocation from bs_sublocations_<location_id>
    try {
      const sublocationKey = `bs_sublocations_${storedReport.relation_ids.location_id}`;
      const sublocations = await localforage.getItem(sublocationKey);
      if (sublocations) {
        const filteredSublocations = sublocations.filter(s => s.id !== storedReport.relation_ids.sublocation_id);
        await localforage.setItem(sublocationKey, filteredSublocations);
        console.log('Sublocation removed from local storage.');
      }
    } catch (error) {
      console.error('Error removing sublocation from bs_sublocations_<location_id>:', error);
    }

    // Remove bs_inspection and bs_inspection_answers
    try {
      await localforage.removeItem('bs_inspection');
      await localforage.removeItem('bs_inspection_answers');
      console.log('Inspection and answers removed from local storage.');
    } catch (error) {
      console.error('Error removing inspection data:', error);
    }

    // Remove the sublocation from bs_sublocations
    try {
      const sublocations = await localforage.getItem('bs_sublocations');
      if (sublocations) {
        const filteredSublocations = sublocations.filter(s => s.id !== storedReport.relation_ids.sublocation_id);
        await localforage.setItem('bs_sublocations', filteredSublocations);
        console.log('Sublocation removed from bs_sublocations.');
      }
    } catch (error) {
      console.error('Error removing sublocation from bs_sublocations:', error);
    }
  }

//#endregion

//#region Handling image uploads (refactored)

  /**
   * Retrieves all images for a specific answer from the Zustand store
   * and uploads them to the server. These requests are batched for 
   * maximum efficiency.
   * 
   * @param {string} answerId - The ID of the answer to upload images for
   * @param {string} reportId - The ID of the report the answer belongs to
   * @returns {Promise<void>} A promise that resolves when all images are uploaded
   * @throws Will throw an error if the uploading of the images fails.
   */
  export const uploadImages = async (answerId, reportId) => {

    const { images, decompressImage, removeImage } = useImageStore.getState();

    const filteredImages = Array.from(images.values())
      .filter(image => image.answerId === answerId)

    if(!filteredImages.length) {
      return;
    }

    const formData = new FormData();
    formData.append('report_id', reportId);

    filteredImages.forEach(image => {
      formData.append(
        'images[]', 
        decompressImage(image.dataUri, image.name)
      );
    })

    try {
      const url = api.inspections.image.store.replace(':answer', answerId);
      await window.axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
        .then(() => filteredImages.forEach(image => removeImage(image.hash)))
    }
    catch(err) {
      console.error('Error uploading images:', err);
      throw new Error('Failed to upload images for answer ID:', answerId);
    }

  }

//#endregion